import React, { useCallback, useEffect, useState } from "react";
import "assets/components/Blog.scss";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.js";
import {
  Grid,
  Typography,
  Autocomplete,
  TextField,
  Pagination,
  PaginationItem,
} from "@mui/material";
import CardBlog from "./CardBlog";
import BlogDetailArticle from "./BlogDetailArticle";
import {
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  Search,
} from "@mui/icons-material";
import { useLocation, useHistory } from "react-router-dom";

const BlogContainer = () => {
  const location = useLocation();
  const history = useHistory();

  const query = new URLSearchParams(location.search);
  const initialPage = parseInt(query.get("page")) || 1;

  const [articles, setArticles] = useState([]);
  const [isDetail, setIsDetail] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState({});
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [articlesPerPage, setArticlesPerPage] = useState(
    window.innerWidth < 600 ? 5 : 9
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    history.replace(`/b?page=${currentPage}`);
  }, [currentPage, history]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setArticlesPerPage(5);
      } else {
        setArticlesPerPage(9);
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const fetchHtmlContent = useCallback(async () => {
    try {
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + "/blog",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: currentPage,
            per_page: articlesPerPage,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Errore");
      }

      const data = await response.json();

      if (data.links && data.links.last) {
        const urlParams = new URLSearchParams(new URL(data.links.last).search);
        const lastPageNumber = parseInt(urlParams.get("page")) || 1;
        setTotalPages(lastPageNumber);
      } else {
        setTotalPages(1);
      }

      setArticles(data.data);
    } catch (error) {
      console.error("Errore", error.message);
    }
  }, [currentPage, articlesPerPage]);

  const fetchSearchResults = useCallback(
    async (keyword) => {
      try {
        if (!keyword) {
          // Se keyword vuota, esci dalla modalità ricerca e carica articoli normali
          setIsSearching(false);
          fetchHtmlContent();
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/blog/search?page=${currentPage}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              keywords: keyword,
              per_page: articlesPerPage,
              page: currentPage,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Errore");
        }

        const data = await response.json();

        setArticles(data.data || []);

        if (data.links && data.links.length) {
          const numericPageLinks = data.links.filter(
            (link) => link.url && !isNaN(parseInt(link.label))
          );

          const lastPageNumber = numericPageLinks.reduce((max, link) => {
            const pageNum = parseInt(
              new URL(link.url).searchParams.get("page")
            );
            return pageNum > max ? pageNum : max;
          }, 1);

          setTotalPages(lastPageNumber);
        } else {
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Errore", error.message);
      }
    },
    [articlesPerPage, currentPage, fetchHtmlContent]
  );

  useEffect(() => {
    if (isSearching) {
      fetchSearchResults(searchInput);
    } else {
      fetchHtmlContent();
    }
  }, [
    currentPage,
    articlesPerPage,
    isSearching,
    fetchHtmlContent,
    fetchSearchResults,
    searchInput,
  ]);

  const options = [];

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSearch = (keyword) => {
    setIsSearching(true);
    setCurrentPage(1);
    setSearchInput(keyword);
  };

  const pagesToShow = new Set([1, currentPage]);
  if (currentPage + 1 <= totalPages) {
    pagesToShow.add(currentPage + 1);
  }

  const pageNumbers = Array.from(pagesToShow).sort((a, b) => a - b);

  return (
    <>
      {isDetail ? (
        <BlogDetailArticle />
      ) : (
        <Grid
          container
          className="Blog"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Grid item xs={12} style={{ padding: "30px", textAlign: "center" }}>
            <Typography className="title">Il blog di DenunciaOnline</Typography>
            <Typography
              style={{ marginTop: "20px" }}
              className="textDescription"
            >
              Tutte le informazioni e le news sul mondo delle denunce e dei
              reati.
            </Typography>
          </Grid>

          {/* Ricerca */}
          <Grid item xs={9} md={4.4} style={{ marginBottom: "50px" }}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs>
                <Autocomplete
                  options={options}
                  freeSolo
                  inputValue={searchInput}
                  onInputChange={(event, newInputValue) => {
                    setSearchInput(newInputValue);
                    if (newInputValue === "") {
                      setIsSearching(false);
                      setCurrentPage(1);
                    }
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleSearch(newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Ricerca per tema o parola chiave"
                      variant="outlined"
                      fullWidth
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearch(searchInput);
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontFamily: '"Quicksand", sans-serif',
                          "& input::placeholder": {
                            fontFamily: '"Quicksand", sans-serif',
                          },
                          "&:hover fieldset": {
                            borderColor: "#20305b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#20305b",
                          },
                        },
                        "& input": {
                          fontFamily: '"Quicksand", sans-serif',
                        },
                      }}
                    />
                  )}
                  clearOnEscape={true}
                />
              </Grid>

              <Grid item>
                <div
                  onClick={() => handleSearch(searchInput)}
                  aria-label="search"
                  color="primary"
                  size="large"
                  style={{ cursor: "pointer" }}
                >
                  <Search style={{ color: "#20305b", marginLeft: "10px" }} />
                </div>
              </Grid>
            </Grid>
          </Grid>

          {/* Articoli */}
          <Grid
            container
            item
            xs={12}
            style={{ marginTop: "30px", marginBottom: "10px" }}
          >
            <Grid container justifyContent="center">
              {articles.map((art) => (
                <CardBlog
                  featured_image={art.featured_image}
                  id={art.id}
                  title={art.title}
                  slug={art.slug}
                  content={art.content}
                  key={art.id}
                />
              ))}
            </Grid>
          </Grid>

          {/* Paginazione  */}
          {articles.length > 0 && (
            <Grid container item xs={9} style={{ marginBottom: "50px" }}>
              {/* Pagina precedente */}
              <Grid item xs={4} style={{ textAlign: "left" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <a
                    href={
                      currentPage > 1
                        ? `/blog?page=${currentPage - 1}`
                        : undefined
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      handlePrev();
                    }}
                    className={`color1 ${
                      currentPage === 1 ? "disabled-link" : ""
                    }`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      textDecoration: "none",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      color: currentPage === 1 ? "gray" : "#20305b",
                    }}
                  >
                    <KeyboardDoubleArrowLeftOutlined
                      style={{ fontSize: "16px", marginTop: "2px" }}
                    />
                    <span
                      style={{ marginLeft: "5px" }}
                      className="text-pagination"
                    >
                      Pagina precedente
                    </span>
                  </a>
                </div>
              </Grid>

              {/* Paginazione numerica */}
              <Grid
                item
                xs={4}
                style={{
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                  renderItem={(item) => {
                    if (!pageNumbers.includes(item.page)) {
                      return null;
                    }

                    return (
                      <PaginationItem
                        {...item}
                        sx={{
                          color: item.selected ? "white" : "#20305b",
                          fontWeight: item.selected ? "700" : "500",
                          backgroundColor: item.selected
                            ? "#20305b !important"
                            : "transparent",
                          "&.Mui-selected": {
                            backgroundColor: "#20305b !important",
                            color: "white !important",
                            fontWeight: "700",
                          },
                          "&:hover": {
                            backgroundColor: item.selected
                              ? "#20305b !important"
                              : "transparent",
                            color: item.selected
                              ? "white !important"
                              : "#20305b",
                            cursor: "default",
                          },
                          "@media (max-width:600px)": {
                            fontSize: "0.75rem",
                            minWidth: 24,
                            height: 24,
                          },
                          "@media (max-width:380px)": {
                            fontSize: "0.75rem",
                            minWidth: 20,
                            height: 20,
                          },
                        }}
                      />
                    );
                  }}
                  sx={{
                    marginTop: "-7px",
                    "& .MuiPaginationItem-previousNext": {
                      display: "none",
                    },
                  }}
                />
              </Grid>

              {/* Pagina successiva */}
              <Grid item xs={4} style={{ textAlign: "right" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <a
                    href={
                      currentPage < totalPages
                        ? `/blog?page=${currentPage + 1}`
                        : undefined
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      handleNext();
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      textDecoration: "none",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      color: currentPage === totalPages ? "gray" : "#20305b",
                    }}
                  >
                    <span
                      style={{ marginRight: "5px" }}
                      className="text-pagination"
                    >
                      Pagina successiva
                    </span>
                    <KeyboardDoubleArrowRightOutlined
                      style={{ fontSize: "16px", marginTop: "2px" }}
                    />
                  </a>
                </div>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default BlogContainer;
